import {
  getTranslationValue,
  Locale,
  translate,
} from '../i18n';
import {
  RentalConsultationFormData,
  RentalConsultationFormErrors,
} from '../types/RentalConsultationForm';

const RENTAL_FORM_DRAFT_STORAGE_KEY = 'delyth_rental_form_draft';

const getLabels = (locale: Locale, key: string) =>
  getTranslationValue<Record<string, string>>(locale, key);

const formatValue = (
  locale: Locale,
  value: string | null | undefined,
  fallback = translate(locale, 'common.notSpecified')
) => (value && value.trim() ? value : fallback);

const formatList = (
  locale: Locale,
  values: string[],
  labelKey?: string,
  fallback = translate(locale, 'common.notSpecified')
) => {
  if (values.length === 0) {
    return fallback;
  }

  const labels = labelKey ? getLabels(locale, labelKey) : {};
  return values.map((value) => labels[value] ?? value).join(', ');
};

const getLabel = (
  locale: Locale,
  labelKey: string,
  value: string,
  fallback = translate(locale, 'common.notSpecified')
) => {
  const labels = getLabels(locale, labelKey);
  return value ? labels[value] ?? value : fallback;
};

export const validateRentalConsultationForm = (
  formData: RentalConsultationFormData,
  locale: Locale
): RentalConsultationFormErrors => {
  const errors: RentalConsultationFormErrors = {};

  if (!formData.firstName.trim()) {
    errors.firstName = translate(locale, 'validation.firstNameRequired');
  }

  if (!formData.lastName.trim()) {
    errors.lastName = translate(locale, 'validation.lastNameRequired');
  }

  if (!formData.email.trim()) {
    errors.email = translate(locale, 'validation.emailRequired');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    errors.email = translate(locale, 'validation.emailInvalid');
  }

  if (!formData.phone.trim()) {
    errors.phone = translate(locale, 'validation.phoneRequired');
  } else if (!/^[+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\s/g, ''))) {
    errors.phone = translate(locale, 'validation.phoneInvalid');
  }

  return errors;
};

export const saveRentalFormDraft = (formData: RentalConsultationFormData) => {
  try {
    localStorage.setItem(RENTAL_FORM_DRAFT_STORAGE_KEY, JSON.stringify(formData));
  } catch (error) {
    console.error('Failed to save rental form draft:', error);
  }
};

export const loadRentalFormDraft = (): RentalConsultationFormData | null => {
  try {
    const draft = localStorage.getItem(RENTAL_FORM_DRAFT_STORAGE_KEY);
    return draft ? JSON.parse(draft) : null;
  } catch (error) {
    console.error('Failed to load rental form draft:', error);
    return null;
  }
};

export const clearRentalFormDraft = () => {
  try {
    localStorage.removeItem(RENTAL_FORM_DRAFT_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear rental form draft:', error);
  }
};

export const buildRentalConsultationEmailSubject = (
  formData: RentalConsultationFormData,
  locale: Locale
): string => {
  const brideName = `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim();
  return brideName
    ? translate(locale, 'rentalConsultation.email.subjectWithName', { name: brideName })
    : translate(locale, 'rentalConsultation.email.subjectDefault');
};

export const buildRentalConsultationEmailBody = (
  formData: RentalConsultationFormData,
  locale: Locale
): string => {
  const submittedAt = new Date().toISOString();

  return [
    translate(locale, 'email.greeting'),
    '',
    translate(locale, 'rentalConsultation.email.intro'),
    '',
    translate(locale, 'email.sections.clientDetails'),
    `${translate(locale, 'email.labels.name')}: ${formatValue(locale, `${formData.firstName} ${formData.lastName}`.trim())}`,
    `${translate(locale, 'email.labels.email')}: ${formatValue(locale, formData.email)}`,
    `${translate(locale, 'email.labels.phone')}: ${formatValue(locale, formData.phone)}`,
    '',
    translate(locale, 'email.sections.weddingDetails'),
    `${translate(locale, 'email.labels.weddingDate')}: ${formatValue(locale, formData.weddingDate)}`,
    `${translate(locale, 'email.labels.venue')}: ${formatValue(locale, formData.venue)}`,
    `${translate(locale, 'rentalConsultation.email.labels.preferredRentalPeriod')}: ${getLabel(locale, 'formOptions.rentalPeriod', formData.preferredRentalPeriod)}`,
    `${translate(locale, 'email.labels.budgetRange')}: ${getLabel(locale, 'formOptions.rentalBudget', formData.budget)}`,
    '',
    translate(locale, 'email.sections.stylePreferences'),
    `${translate(locale, 'rentalConsultation.email.labels.preferredStyles')}: ${formatList(locale, formData.preferredStyles, 'formOptions.dressSilhouettes')}`,
    `${translate(locale, 'email.labels.currentDressSize')}: ${formatValue(locale, formData.dressSize)}`,
    '',
    translate(locale, 'email.sections.additionalNotes'),
    `${translate(locale, 'email.labels.specialRequests')}: ${formatValue(locale, formData.specialRequests)}`,
    `${translate(locale, 'email.labels.howSheHeard')}: ${getLabel(locale, 'formOptions.howDidYouHear', formData.howDidYouHear)}`,
    '',
    translate(locale, 'email.sections.systemDetails'),
    `${translate(locale, 'email.labels.submittedAt')}: ${submittedAt}`,
    `${translate(locale, 'email.labels.source')}: rental_consultation_form`,
  ].join('\n');
};

export const buildRentalConsultationMailtoLink = (
  formData: RentalConsultationFormData,
  recipientEmail: string,
  locale: Locale
): string => {
  const subject = encodeURIComponent(buildRentalConsultationEmailSubject(formData, locale));
  const body = encodeURIComponent(buildRentalConsultationEmailBody(formData, locale));

  return `mailto:${recipientEmail}?subject=${subject}&body=${body}`;
};

export const generateRentalConsultationSummary = (
  formData: RentalConsultationFormData,
  locale: Locale
): string => {
  return [
    `${translate(locale, 'email.labels.name')}: ${formData.firstName} ${formData.lastName}`.trim(),
    `${translate(locale, 'email.labels.email')}: ${formData.email}`,
    `${translate(locale, 'email.labels.phone')}: ${formData.phone}`,
    `${translate(locale, 'email.labels.weddingDate')}: ${formatValue(locale, formData.weddingDate)}`,
    `${translate(locale, 'email.labels.venue')}: ${formatValue(locale, formData.venue)}`,
    `${translate(locale, 'rentalConsultation.email.labels.preferredRentalPeriod')}: ${getLabel(locale, 'formOptions.rentalPeriod', formData.preferredRentalPeriod)}`,
    `${translate(locale, 'email.labels.budget')}: ${getLabel(locale, 'formOptions.rentalBudget', formData.budget)}`,
    `${translate(locale, 'rentalConsultation.email.labels.preferredStyles')}: ${formatList(locale, formData.preferredStyles, 'formOptions.dressSilhouettes')}`,
    `${translate(locale, 'email.labels.size')}: ${formatValue(locale, formData.dressSize)}`,
    `${translate(locale, 'email.labels.specialRequests')}: ${formatValue(locale, formData.specialRequests)}`,
    `${translate(locale, 'email.labels.howTheyHeard')}: ${getLabel(locale, 'formOptions.howDidYouHear', formData.howDidYouHear)}`,
  ].join('\n');
};
